import { EChartsOption } from 'echarts/types/dist/shared';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { PieChart } from 'echarts/charts';
import { ScoreRangeColors, ScoreRanges, scoreRangeLabels } from '../../constants/charts.constants';
import { EchartsService } from '../../services/echarts.service';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-score-chart',
  templateUrl: './score-chart.component.html',
  styleUrls: ['./score-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EchartsService],
  standalone: false,
})
export class ScoreChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public score: number;
  @Input() public computedDate: string;
  @Input() public hideLegend: boolean;
  @Input() public label?: string;

  @ViewChild('scoreChartTarget') private scoreChartTarget: ElementRef<HTMLDivElement>;
  @ViewChild('svgPatterns') private svgPatterns: ElementRef<HTMLElement>;

  public scoreRanges: Record<scoreRangeLabels, number[]>;
  public scoreRangeLabels: typeof scoreRangeLabels;
  public colors: Record<scoreRangeLabels, string>;

  private pieChart: echarts.EChartsType;

  constructor(
    private ref: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private echartsService: EchartsService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.scoreRangeLabels = scoreRangeLabels;
    this.scoreRanges = ScoreRanges;
    this.colors = ScoreRangeColors;
  }

  public get useDsScore(): boolean {
    return this.featureFlagService.variation(FeatureFlagCollection.scoreDesignSystem, true);
  }

  public get scoreClass(): string {
    if (this.hideLegend) {
      return 'no-legend';
    }

    if (this.score >= this.scoreRanges[scoreRangeLabels.low][0] && this.score <= this.scoreRanges[scoreRangeLabels.low][1]) {
      return scoreRangeLabels.low;
    } else if (
      this.score >= this.scoreRanges[scoreRangeLabels.mid][0] &&
      this.score <= this.scoreRanges[scoreRangeLabels.mid][1]
    ) {
      return scoreRangeLabels.mid;
    }
    return scoreRangeLabels.high;
  }

  private initChart(): void {
    if (!this.scoreChartTarget) {
      return;
    }

    if (this.pieChart) {
      this.pieChart.dispose();
    }

    this.changeDetectorRef.detectChanges();

    const option: EChartsOption = {
      series: {
        type: 'pie',
        radius: [55, 70],
        left: 'center',
        width: '100%',
        animation: false,
        silent: true,
        itemStyle: {
          borderColor: '#FFFFFF',
          borderWidth: 0,
        },
        label: {
          show: false,
        },
        labelLine: {},
        data: [
          {
            value: this.score,
          },
          {
            value: 100 - this.score,
          },
        ],
      },
    };

    echarts.use([PieChart, SVGRenderer]);
    const elChart: HTMLDivElement = this.scoreChartTarget.nativeElement;
    this.pieChart = this.echartsService.initialize(elChart);
    this.pieChart.on('finished', () => {
      const xmlns: string = 'http://www.w3.org/2000/svg';
      const scoreLabelWrap: SVGGElement = document.createElementNS(xmlns, 'g') as SVGGElement;
      scoreLabelWrap.setAttribute('class', 'score-label');
      scoreLabelWrap.setAttribute('data-test-id', 'score-label');
      const scoreLabel: SVGTextElement = document.createElementNS(xmlns, 'text') as SVGTextElement;
      scoreLabel.setAttribute('dx', '50%');
      scoreLabel.setAttribute('dy', '50%');
      scoreLabel.setAttribute('text-anchor', 'middle');
      scoreLabel.textContent = String(this.score);
      scoreLabelWrap.appendChild(scoreLabel);
      this.pieChart.getDom().getElementsByTagName('svg')[0].appendChild(scoreLabelWrap);
    });
    this.pieChart.setOption(option);
  }

  public ngAfterViewInit(): void {
    this.ref.nativeElement.querySelectorAll('svg.square').forEach((i: HTMLElement) => {
      i.appendChild(this.svgPatterns.nativeElement);
    });
    this.initChart();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('score' in changes && changes.score.previousValue !== changes.score.currentValue) {
      this.initChart();
    }
  }

  public ngOnDestroy(): void {
    if (this.pieChart) {
      this.pieChart.off('click');
    }
  }
}
